import type { Route } from '@angular/router';
import type { TeamMembersOptions } from '../org-team-members/team-members.type';
import { orgTeamRoutes, type OrgTeamRoutesOptions } from '../org-teams/org-team.routes';

export interface OrgSettingsRoutesOptions {
  /** 감사 로그 화면 제목(화면 h1과 라우트 `title`=문서 제목). 생략하면 화면은 '변경 이력', 문서 제목은 앱 기본. */
  auditLogTitle?: string;
  /** 구성원 화면 제목(화면 h1과 라우트 `title`=문서 제목). 생략하면 화면은 '구성원', 문서 제목은 앱 기본. */
  membersTitle?: string;
  /** 구성원 화면에 보여줄 역할(예: `['Member']`). 생략하면 org 직속 구성원 전체. */
  membersRoles?: readonly string[];
  /**
   * 구성원 화면의 "멤버 추가" 버튼을 켠다(`POST /orgs/:orgId/members`). **기본은 꺼짐**이다.
   * `email: false`면 전화번호로만 추가한다 — 이메일 좌석이 없는 schoolbell-e-api를 쓰는 앱이 끈다.
   */
  membersAdd?: boolean | { email?: boolean };
  /**
   * 팀 멤버 화면(`team-members`, `OrgTeamMembersComponent`)을 켠다 — 단체 + 팀 구조 전체의 학생 명단을 한 트리로 보여 주는 읽기 전용 화면(팀·멤버 분류·이름 검색 필터). **기본은 꺼짐**이다.
   * 앱마다 표 모양이 다르므로 컬럼을 선언형으로 받는다: `numberColumn`(이름 앞 추가 열 — 열 이름 + 응답의 어느 값을 보일지 점 표기 경로, 예: schoolbell-e `{ label: '번호', valuePath: 'meta.memberNum' }`),
   * `columns`(연락처 컬럼들 — 헤더 + 본인(`'self'`)/자리 번호(`{ priority }`)), `studentsOnly`(기본 true), `title`.
   */
  teamMembers?: boolean | TeamMembersOptions;
  /**
   * 멤버 분류(라벨) 화면(`labels`, `OrgLabelsComponent`)을 켠다 — 분류 목록 CRUD(이름 인라인 변경·삭제), 고른 분류에 속한 멤버의 팀 트리 명단, 만들기/수정 모달(org·팀 구조 전체 멤버 체크박스 배정).
   * **기본은 꺼짐**이다. 자리(Member) 레벨 라벨만 다루고, 연동 단체의 외부(학교종이) 라벨은 읽기 전용이다. `title`로 화면 제목(h1과 라우트 `title`)을 바꾼다(기본 '멤버 분류').
   */
  labels?: boolean | { title?: string };
  /**
   * 단체 소유(OrgManaged) 계정 관리 화면(`managed-users`)을 켠다 — 학교가 학생에게 나눠 주는 로그인 코드로만 로그인하는 계정을 이름 목록으로 한꺼번에 만들고 코드를 인쇄·저장·재발급한다.
   * **기본은 꺼짐**이다 — 백엔드가 소유 계정 생성 API를 쓸 수 있고 학생이 로그인할 앱(`SpinoffBffModule.forRoot({ orgManagedLogin: true })` + `orgManagedLoginRoute()`)만 켠다.
   * `title`로 화면 제목(h1과 라우트 `title`)을 바꾼다(기본 '학생 계정').
   */
  managedUsers?: boolean | { title?: string };
  /**
   * 학교종이 단체 연동 화면(`schoolbell-org-link`, `OrgSchoolbellLinkComponent`)을 켠다 — 연결 상태·연결 시작·권한 갱신·해제 안내.
   * **기본은 꺼짐**이다 — 학교종이 연동(`SpinoffProviderFederationModule`의 org-link 컨트롤러)을 쓰는 앱만 켠다.
   * 다른 화면 안에 끼워 쓰려면 라우트 대신 `OrgSchoolbellLinkComponent`를 직접 import한다. `title`로 화면 제목(h1과 라우트 `title`)을 바꾼다(기본 '학교종이 단체 연동').
   */
  schoolbellLink?: boolean | { title?: string };
  /**
   * 팀(학급) 구조(`teams`)·팀 명단(`teams/:teamId/members`) 화면을 켠다. **기본은 꺼짐**이다 — 팀 구조를 쓰는 앱만 켠다.
   * 세부 옵션(`structureTitle`·`structureEditable`·`roster` 등)은 `OrgTeamRoutesOptions` 그대로 받는다(`true`면 기본값).
   * 팀 입장 주소(`orgTeamEntryRoute`)·팀 단위 뷰(`orgTeamViewRoute`)는 이 옵션과 별개 라우트라 앱이 따로 선언한다.
   */
  teams?: boolean | OrgTeamRoutesOptions;
  /**
   * 서비스별 세부 권한(OrgPermission) 부여·회수 화면(`permissions`, `OrgPermissionsComponent`)을 켠다. **기본은 꺼짐**이다 —
   * 백엔드의 `orgs/:orgId/permissions` 라우트(`SpinoffOrgPermissionsController`/schoolbell-e-api 동등 컨트롤러)를 쓰고 단체 관리자에게 이 화면을 보일 앱만 켠다.
   * `title`로 화면 제목(h1과 라우트 `title`)을 바꾼다(기본 '권한 설정').
   */
  permissions?: boolean | { title?: string };
}

/**
 * `orgs/:orgId` 라우트의 children으로 펼쳐 쓰는 단체 설정 화면 라우트 모음. 앱마다 같은 화면을
 * 돌려쓰도록 한곳에 둔다 — 프리픽스 구조는 `tools/ci/lint-angular-org-route-structure.mjs`가
 * 강제한다.
 *
 * ```ts
 * { path: 'orgs/:orgId', children: [...orgSettingsRoutes(), ...앱 고유 라우트] }
 * ```
 *
 * 전제: 앱이 `withComponentInputBinding()`과 `withRouterConfig({ paramsInheritanceStrategy:
 * 'always' })`를 켜고, 생성 클라이언트(`SpinoffPlatformOrgContractService`)의 상대 경로 요청이 API
 * 오리진으로 향하도록 구성돼 있어야 한다.
 */
export function orgSettingsRoutes(options: OrgSettingsRoutesOptions = {}): Route[] {
  const membersAdd = options.membersAdd === true ? {} : options.membersAdd || undefined;
  const teamMembers = options.teamMembers === true ? {} : options.teamMembers || undefined;
  const labels = options.labels === true ? {} : options.labels || undefined;
  const managed = options.managedUsers === true ? {} : options.managedUsers || undefined;
  const schoolbellLink = options.schoolbellLink === true ? {} : options.schoolbellLink || undefined;
  const teams = options.teams === true ? {} : options.teams || undefined;
  const permissions = options.permissions === true ? {} : options.permissions || undefined;
  return [
    ...(teams ? orgTeamRoutes(teams) : []),
    {
      path: 'audit-log',
      title: options.auditLogTitle,
      data: options.auditLogTitle ? { pageTitle: options.auditLogTitle } : undefined,
      loadComponent: () => import('./audit-log/org-audit-log.component').then((m) => m.OrgAuditLogComponent),
    },
    {
      path: 'members',
      title: options.membersTitle,
      data: {
        ...(options.membersTitle ? { pageTitle: options.membersTitle } : {}),
        ...(options.membersRoles ? { roles: options.membersRoles } : {}),
        ...(membersAdd ? { memberAdd: true, memberAddEmail: membersAdd.email !== false } : {}),
      },
      loadComponent: () => import('./members/org-members.component').then((m) => m.OrgMembersComponent),
    },
    ...(teamMembers
      ? [
          {
            path: teamMembers.path ?? 'team-members',
            title: teamMembers.title,
            data: {
              ...(teamMembers.title ? { pageTitle: teamMembers.title } : {}),
              ...(teamMembers.studentsOnly !== undefined ? { teamMembersStudentsOnly: teamMembers.studentsOnly } : {}),
              ...(teamMembers.numberColumn ? { teamMembersNumberColumn: teamMembers.numberColumn } : {}),
              ...(teamMembers.columns ? { teamMembersColumns: teamMembers.columns } : {}),
              ...(teamMembers.reorderable ? { teamMembersReorderable: true } : {}),
              ...(teamMembers.addStudentPath ? { teamMembersAddStudentPath: teamMembers.addStudentPath } : {}),
              ...(teamMembers.editable ? { teamMembersEditable: true } : {}),
              ...(teamMembers.saveMode === 'batch' ? { teamMembersSaveMode: 'batch' } : {}),
            },
            loadComponent: () => import('../org-team-members/org-team-members.component').then((m) => m.OrgTeamMembersComponent),
          } satisfies Route,
        ]
      : []),
    ...(labels
      ? [
          {
            path: 'labels',
            title: labels.title,
            data: labels.title ? { pageTitle: labels.title } : undefined,
            loadComponent: () => import('../org-labels/org-labels.component').then((m) => m.OrgLabelsComponent),
          } satisfies Route,
        ]
      : []),
    ...(managed
      ? [
          {
            path: 'managed-users',
            title: managed.title,
            data: managed.title ? { pageTitle: managed.title } : undefined,
            loadComponent: () => import('./managed-users/org-managed-users.component').then((m) => m.OrgManagedUsersComponent),
          } satisfies Route,
        ]
      : []),
    ...(schoolbellLink
      ? [
          {
            path: 'schoolbell-org-link',
            title: schoolbellLink.title,
            data: schoolbellLink.title ? { pageTitle: schoolbellLink.title } : undefined,
            loadComponent: () => import('./schoolbell-link/org-schoolbell-link.component').then((m) => m.OrgSchoolbellLinkComponent),
          } satisfies Route,
        ]
      : []),
    ...(permissions
      ? [
          {
            path: 'permissions',
            title: permissions.title,
            data: permissions.title ? { pageTitle: permissions.title } : undefined,
            loadComponent: () => import('./permissions/org-permissions.component').then((m) => m.OrgPermissionsComponent),
          } satisfies Route,
        ]
      : []),
  ];
}
